/**
 * Iter-340c — Communications Health Tile.
 * Aggregated environment + delivery status for outbound email + SMS.
 * Live "Send Test Email" and "Send Test SMS to Me" actions hit the real
 * production pipeline so an operator can distinguish an env-guard
 * mis-config from a downstream SendGrid/Twilio failure without leaving
 * the admin UI.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Mail, MessageSquare, AlertTriangle,
  CheckCircle2, Send, ShieldAlert,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const LIGHT_COLOR = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-rose-500',
};
const LIGHT_LABEL = {
  green: 'Healthy',
  amber: 'Degraded',
  red: 'Blocked',
};

function TrafficDot({ status }) {
  return (
    <span
      className={`inline-block w-3 h-3 rounded-full ${LIGHT_COLOR[status] || 'bg-slate-300'}`}
      data-testid={`comm-health-light-${status || 'unknown'}`}
      title={LIGHT_LABEL[status] || 'Unknown'}
    />
  );
}

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function CommHealthTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyEmail, setBusyEmail] = useState(false);
  const [busySms, setBusySms] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [lastEmailResult, setLastEmailResult] = useState(null);
  const [lastSmsResult, setLastSmsResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/system/comm-health`, AUTH());
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load comm health');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const sendTestEmail = async () => {
    setBusyEmail(true);
    setLastEmailResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/system/comm-health/test-email`,
        { to_email: testEmail || undefined },
        AUTH(),
      );
      setLastEmailResult(r.data);
      const status = r.data.provider_response?.status;
      if (status === 'sent' || status === 'queued') {
        toast.success(`Email dispatched to ${r.data.to_email}`);
      } else {
        toast.error(`Email ${status || 'failed'}: ${r.data.provider_response?.message || 'see details'}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test email failed');
    } finally { setBusyEmail(false); }
  };

  const sendTestSms = async () => {
    setBusySms(true);
    setLastSmsResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/system/comm-health/test-sms`,
        { to_phone: testPhone || undefined },
        AUTH(),
      );
      setLastSmsResult(r.data);
      const status = r.data.provider_response?.status;
      if (status === 'sent' || status === 'queued' || status === 'sandbox') {
        toast.success(`SMS dispatched to ${r.data.to_phone}`);
      } else {
        toast.error(`SMS ${status || 'failed'}: ${r.data.provider_response?.message || 'see details'}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test SMS failed');
    } finally { setBusySms(false); }
  };

  if (loading && !data) {
    return (
      <Card className="p-6 flex items-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading communications health…</span>
      </Card>
    );
  }
  if (!data) return null;

  const env = data.environment;
  const email = data.email;
  const sms = data.sms;

  return (
    <Card className="p-5" data-testid="comm-health-tile">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <TrafficDot status={data.overall_status} />
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Ops · Communications</div>
            <h2 className="text-lg font-bold" data-testid="comm-health-title">
              Communications Health · {LIGHT_LABEL[data.overall_status] || 'Unknown'}
            </h2>
          </div>
        </div>
        <Button
          variant="outline" size="sm" onClick={load}
          data-testid="comm-health-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Environment row */}
      <div
        className={`rounded-lg p-3 mb-4 ${env.is_production ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}
        data-testid="comm-health-env-block"
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          {env.is_production
            ? <><CheckCircle2 className="w-4 h-4 text-emerald-700" /> Production mode</>
            : <><ShieldAlert className="w-4 h-4 text-amber-700" /> Non-production mode ({env.reason})</>}
        </div>
        <div className="mt-1 text-xs text-slate-700 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
          <div><b>APP_ENV:</b> {env.APP_ENV ?? '∅'}</div>
          <div className="truncate"><b>PUBLIC_APP_URL:</b> {env.PUBLIC_APP_URL ?? '∅'}</div>
        </div>
        {env.issues && env.issues.length > 0 && (
          <div className="mt-2 space-y-1">
            {env.issues.map((i, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 text-xs bg-white/70 border border-rose-200 rounded p-2"
                data-testid={`comm-health-issue-${i.code}`}
              >
                <AlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-rose-800">{i.severity} · {i.code}</div>
                  <div className="text-slate-700">{i.detail}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Email + SMS side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChannelCard
          icon={<Mail className="w-4 h-4" />}
          title="Email · SendGrid"
          status={email.traffic_light}
          rows={[
            ['Provider', email.sendgrid_configured ? 'Configured' : 'Missing key'],
            ['Sender', email.sender_email],
            ['Env pause', email.env_paused ? 'ON' : 'off'],
            ['DB pause', email.db_paused ? 'ON' : 'off'],
            ['Last delivered', fmtTime(email.last_successful_at)],
          ]}
          recent={email.recent_24h}
          lastFailure={email.last_failure && {
            at: email.last_failure.at,
            to: email.last_failure.to_email,
            status: email.last_failure.status,
            code: email.last_failure.status_code,
            msg: email.last_failure.error_message,
          }}
          testInputPlaceholder="Send to (defaults to your email)"
          testInputValue={testEmail}
          onTestInputChange={setTestEmail}
          onSendTest={sendTestEmail}
          sendBusy={busyEmail}
          sendTestId="comm-health-send-test-email"
          inputTestId="comm-health-test-email-input"
          lastResult={lastEmailResult}
        />
        <ChannelCard
          icon={<MessageSquare className="w-4 h-4" />}
          title="SMS · Twilio"
          status={sms.traffic_light}
          rows={[
            ['Provider', sms.twilio_configured ? 'Configured' : 'Missing creds'],
            ['Env pause', sms.env_paused ? 'ON' : 'off'],
            ['Last delivered', fmtTime(sms.last_successful_at)],
          ]}
          recent={sms.recent_24h}
          lastFailure={sms.last_failure && {
            at: sms.last_failure.at,
            to: sms.last_failure.phone,
            status: sms.last_failure.status,
            code: sms.last_failure.error_code,
            msg: sms.last_failure.error_message,
          }}
          testInputPlaceholder="+1XXXXXXXXXX (defaults to your phone)"
          testInputValue={testPhone}
          onTestInputChange={setTestPhone}
          onSendTest={sendTestSms}
          sendBusy={busySms}
          sendTestId="comm-health-send-test-sms"
          inputTestId="comm-health-test-sms-input"
          lastResult={lastSmsResult}
        />
      </div>

      <div className="mt-4 text-[11px] text-slate-500">
        Auto-refreshes every 60s · Generated {fmtTime(data.generated_at)}
      </div>
    </Card>
  );
}

function ChannelCard({
  icon, title, status, rows, recent, lastFailure,
  testInputPlaceholder, testInputValue, onTestInputChange, onSendTest,
  sendBusy, sendTestId, inputTestId, lastResult,
}) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <TrafficDot status={status} />
        {icon}
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <div className="text-slate-500">{k}</div>
            <div className="text-slate-800 truncate" title={String(v ?? '')}>{v ?? '—'}</div>
          </React.Fragment>
        ))}
      </div>
      {recent && Object.keys(recent).length > 0 && (
        <div className="mt-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Last 24h</div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(recent).map(([status, n]) => (
              <span
                key={status}
                className={`px-2 py-0.5 rounded-full text-[11px] border ${status.startsWith('block') || status === 'paused' || status === 'error' || status === 'failed' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-slate-50 border-slate-200 text-slate-700'}`}
                data-testid={`comm-health-recent-${title.toLowerCase().replace(/\W/g, '-')}-${status}`}
              >
                {status}: {n}
              </span>
            ))}
          </div>
        </div>
      )}
      {lastFailure && (
        <div className="mt-2 text-[11px] bg-rose-50 border border-rose-200 rounded p-2">
          <div className="font-semibold text-rose-800">Last failure · {lastFailure.status}{lastFailure.code ? ` (${lastFailure.code})` : ''}</div>
          <div className="text-slate-700 mt-0.5">to: {lastFailure.to}</div>
          <div className="text-slate-700">at: {fmtTime(lastFailure.at)}</div>
          {lastFailure.msg && <div className="text-slate-700 mt-0.5 break-words">{lastFailure.msg}</div>}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Input
          placeholder={testInputPlaceholder}
          value={testInputValue}
          onChange={(e) => onTestInputChange(e.target.value)}
          className="text-xs h-8"
          data-testid={inputTestId}
        />
        <Button
          size="sm" onClick={onSendTest} disabled={sendBusy}
          data-testid={sendTestId}
        >
          {sendBusy
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Sending</>
            : <><Send className="w-3.5 h-3.5 mr-1.5" /> Send test</>}
        </Button>
      </div>
      {lastResult && (
        <details className="mt-2 text-[11px]" data-testid={`${sendTestId}-result`}>
          <summary className="cursor-pointer text-slate-500">Last provider response</summary>
          <pre className="mt-1 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(lastResult.provider_response, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
